/**
 * Shared style tokens + formatting helpers for the bespoke
 * pay-by-bank-backoffice components (PRD §12, measured live from the deployed
 * sandbox). Font is Inter throughout — matches forms-host's dev default, so the
 * Inter-vs-Roboto trap from `un-bulk-upload` does not apply here.
 *
 * The neutral greys below are the exact measured values; the accent/primary
 * button colours come from the injected tenant theme via the shared
 * `AccentButton`/`PrimaryButton` wrappers, not from here.
 */

// --- Measured palette (PRD §12) --------------------------------------------
export const INK = '#1E1E1E'; // titles, values, amounts
export const GREY = '#696969'; // section headings, data-item labels, row time
export const GREY_2 = '#434343'; // counter labels
export const GREY_3 = '#8F8F8F'; // amount currency suffix
export const GROUP_BG = '#F4F4F4'; // date group-header pill
export const FIELD_BG = '#F2F5F7'; // search input, radio
export const HAIRLINE = '#EAEAEA'; // detail header rule / row divider

/**
 * Locale-formatted money amount (2 decimals) for the **totals** and the
 * **detail header**, e.g. `148,30` under es (PRD §6). The live app renders
 * these with the operator locale's separators (comma under es).
 */
export const formatAmount = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/**
 * Money amount for a **list row**, e.g. `26.00`. The live app renders row
 * amounts with a dot decimal regardless of the operator locale (unlike the
 * totals/detail, which follow the locale) — matched exactly here.
 */
export const formatRowAmount = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/** `HH:mm` in the operator locale, for a list row (PRD §4b). */
export const formatRowTime = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** Day header for a date group, e.g. `7 de mayo de 2026` under es (PRD §4b). */
export const formatGroupDate = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(iso),
  );

/**
 * `D de MMMM de YYYY, HH:mm` for the detail header (PRD §4d). Composed as
 * date + ", " + time to match the live exactly (a single Intl call under es
 * inserts "a las" between the date and time, which the live does not use).
 */
export const formatDetailDateTime = (iso: string, locale: string): string =>
  `${formatGroupDate(iso, locale)}, ${formatRowTime(iso, locale)}`;
